import React, { useCallback, useEffect, useState } from "react";
import Loader from "../../../../components/common/Loader";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { Controller, useForm } from "react-hook-form";
import InputAndSelect from "../../../../components/common/InputAndSelect";
import { savedPayoutV2Data } from "../../redux/formDataSlice";
import { getMomoPayoutTransferFee, getPayoutTransferFee } from "../../redux/slice";
import { ContentWrapper, ContinueButton, RateBox, Error } from "../styles";
import { formatCurrencyAmount } from "../../../../utils/currency";
import { Link } from "react-router-dom";
import ErrorMessage from "../../../../components/ErrorMessage";
import { capitalizeCurrencyCode } from "../../../../utils/helpers";
import { Button } from "../../../../components/common/Button";
import CustomSelect from "../../../../components/common/CustomSelect";
import { TZSPayouts } from "../../const";

type SelectedBankObject = {
    label: string;
    value: string;
    originalCode?: string;
};

type AmountType = {
    amount?: number;
    bankName?: string;
    handleNext?: () => void;
    currency?: string;
    totalAmount?: number;
    balanceError?: boolean;
    fees?: string;
    bankCode?: string;
    selectedBank?: SelectedBankObject;
};

const Amount = ({ handleNext }: AmountType) => {
    const dispatch = useAppDispatch();

    const { control, handleSubmit, setValue, watch, formState: { errors, isValid }, } = useForm<AmountType>();

    const INT_FORM_DATA = useAppSelector(
        (state) => state.formData?.payoutv2Data || {},
    );

    const [localError, setLocalError] = useState(false);
    const [userData, setUserData] = useState<AmountType>(INT_FORM_DATA);
    const [showMinimumError, setShowMinimumError] = useState<boolean>();
    const [currentWallet, setCurrentWallet] = useState(null);
    const { wallets, loading } = useAppSelector((state) => state?.wallets || {});
    const getSavedData = useAppSelector((state) => state.formData.payoutv2Data);

    const bankTypeWatch = watch("bankCode")
    const amountWatch = watch('amount')

    const minValue = 100

    useEffect(() => {
        if (userData.amount && userData?.amount < minValue) {
            setShowMinimumError(true);
        } else if (userData.totalAmount && userData.totalAmount > currentWallet?.balance) {
            setLocalError(true);
            setShowMinimumError(false);
        } else {
            setShowMinimumError(false);
            setLocalError(false);
        }
    }, [userData.amount, userData.totalAmount]);

    useEffect(() => {
        setCurrentWallet(
            wallets?.find((wallet) => wallet.currency === "TZS")
        );
    }, [wallets]);

    const handleDataUpdate = (field: keyof AmountType, value: any) => {
        setUserData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    useEffect(() => {
        if (!amountWatch) return;

        const timeoutId = setTimeout(async () => {
            if (bankTypeWatch === "TZ-BANK-B2C") {
                const resultAction = await dispatch(getPayoutTransferFee("TZ"));
                if (getPayoutTransferFee.fulfilled.match(resultAction)) {
                    const { fees } = resultAction.payload; 
                    handleDataUpdate("fees", fees);
                }
            } 
            else {
                const resultAction = await dispatch(getMomoPayoutTransferFee({ country: "TZ", amount: amountWatch }));
                if (getMomoPayoutTransferFee.fulfilled.match(resultAction)) {
                    const { fees } = resultAction.payload;
                    handleDataUpdate("fees", fees);
                }
            }
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [amountWatch, bankTypeWatch]);

    useEffect(() => {
        if (getSavedData) {
            Object.keys(getSavedData).forEach((key) => {
                setValue(
                    key as keyof AmountType,
                    getSavedData[key as keyof AmountType],
                    { shouldValidate: true }
                );
                setValue('bankName', getSavedData?.bankCode, { shouldValidate: true })
                setValue('fees', getSavedData?.fees, { shouldValidate: true })
                setValue('totalAmount', getSavedData?.totalAmount, { shouldValidate: true })
            });
        }
    }, [getSavedData, setValue]);

    const handleNextStep = useCallback(
        (data: AmountType) => {
            const selectedBank = TZSPayouts.find((b) => b.value === data.bankCode)

            const combinedData = {
                ...userData,
                ...data,
                bankName: selectedBank?.label,
                bankCode: data.bankCode,
                selectedBank: selectedBank,
                fees: userData.fees,
                totalAmount: userData.totalAmount
            };

            dispatch(savedPayoutV2Data(combinedData));
            handleNext?.();
        },
        [dispatch, userData],
    );

    const filteredWallets = wallets as AmountType[];

    const uniqueCurrencies = Array.from(
        new Set(filteredWallets?.map((wallet) => wallet.currency)),
    );

    const options = uniqueCurrencies.map((currency) => ({
        label: currency,
        value: currency,
    }));

    const walletToDebit = options?.filter((option) => option.value === "TZS");

    useEffect(() => {
        if (userData.amount) {
            handleDataUpdate(
                "totalAmount",
                Number(userData.amount) + Number(userData.fees),
            );
        }
    }, [userData.amount, userData.fees]);

    const handleBankChange = (selectedModifiedValue) => {
        const selectedBank = TZSPayouts.find((b) => b.value === selectedModifiedValue)

        handleDataUpdate("bankName", selectedBank?.label);
        handleDataUpdate("selectedBank", selectedBank);
        handleDataUpdate("bankCode", selectedBank.value)
    };

    const disableContinueButton = userData.balanceError || loading || !isValid || localError || showMinimumError

    return (
        <ContentWrapper>
            <Loader isLoading={loading} />

            <div>
                <Controller
                    name="bankCode"
                    control={control}
                    rules={{
                        required: "Please input the beneficiary's Bank name",
                    }}
                    render={({ field }) => (
                        <CustomSelect
                            placeholder={`Select bank`}
                            $width="100%"
                            field={field}
                            disableLabel
                            options={TZSPayouts}
                            onChange={(selectedValue) => {
                                field.onChange(selectedValue)
                                handleBankChange(selectedValue)
                            }}
                            showSearch
                            filterOption={(inputValue, option) =>
                                option.label.toLowerCase().includes(inputValue.toLowerCase())
                            }
                            lightBorder={true}
                            value={field.value}
                        />
                    )}
                />
            </div>
            <div>

                <Controller
                    name="amount"
                    rules={{ required: "Amount is required" }}
                    control={control}
                    render={({ field }) => (
                        <InputAndSelect
                            placeholder="Amount"
                            amountValue={field.value}
                            onAmountChange={(value) => {
                                field.onChange(value)
                                handleDataUpdate("amount", value)
                            }}
                            selectValue={"TZS"}
                            width="100%"
                            options={walletToDebit}
                        />
                    )}
                />
            </div>

            {showMinimumError && (
                <div className="min">
                    <ErrorMessage
                        message={`Minimum amount: ${capitalizeCurrencyCode("TZS")} ${new Intl.NumberFormat().format(
                            minValue
                        )}`}
                    />
                </div>
            )}
            {errors?.amount && <Error>{errors?.amount?.message}</Error>}

            {userData.amount && <section>
                <RateBox>
                    <span className="fee-box">
                        +
                        <span>
                            {formatCurrencyAmount("TZS", userData?.fees ?? 0, 2) ?? 0}
                        </span>
                    </span>
                    <span>Processing fee</span>
                </RateBox>
            </section>}

            {userData.amount && (
                <section>
                    <RateBox>
                        <span className="fee-box">
                            <span>
                                {formatCurrencyAmount("TZS", userData.totalAmount, 2) ?? 0}
                            </span>
                        </span>
                        <span>Total amount</span>
                    </RateBox>
                </section>
            )}

            {localError && (
                <div className="min">
                    <span>Exceeds your wallet balance of {formatCurrencyAmount("TZS", currentWallet?.balance, 2) ?? 0}.</span>
                    <Link to="/wallets"> Top-up funds</Link>
                </div>
            )}

            <ContinueButton>
                <span className="form-label">
                    Funds should arrive within 1-3 working days.
                </span>

                <Button
                    label="Continue"
                    height="48px"
                    fontSize="14px"
                    disabled={disableContinueButton}
                    onClick={handleSubmit(handleNextStep)}
                />
            </ContinueButton>

        </ContentWrapper>
    )
}

export default Amount